import React, { useCallback, useState } from 'react';
import Card from '../../../ui/Card';
import { UnitTable } from './UnitTable';
import type { Floorplan, Unit } from './UnitTable';
import { PhotoLightbox } from '../MediaCenter/PhotoLightbox';
import * as styles from './unitsModule.styles';

type FloorPlanCardProps = {
  floorplan: Floorplan;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  compact?: boolean;
};

const getFloorplanImageUrl = (floorplan: Floorplan): string | null => {
  // Only show actual floor plan layout images, not unit interior photos.
  // FloorplanModel merges layouts (floor plan diagrams) and photos (unit shots)
  // into a single array with layouts first. Skip if no layouts exist.
  const layoutCount = floorplan.layoutCount ?? 0;
  if (layoutCount === 0) return null;

  if (floorplan.largePhotos?.length > 0) return floorplan.largePhotos[0].url;
  if (floorplan.photos?.length > 0) return floorplan.photos[0].url;
  return null;
};

export const FloorPlanCard = React.memo(({ floorplan, onUnitSelect, compact }: FloorPlanCardProps) => {
  const imageUrl = getFloorplanImageUrl(floorplan);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleImageClick = useCallback(() => {
    setLightboxOpen(true);
  }, []);

  const lightboxPhotos = imageUrl ? [{ url: imageUrl, caption: `Floor plan ${floorplan.name || ''}` }] : [];

  return (
    <Card variant="default" size="md" className={compact ? styles.compactCard : undefined}>
      {imageUrl && (
        <div className={compact ? styles.compactImageWrapper : styles.floorPlanImageWrapper}>
          <button
            type="button"
            className={styles.floorPlanImageButton}
            onClick={handleImageClick}
            aria-label={`View floor plan ${floorplan.name || ''}`}
          >
            <img src={imageUrl} alt={`Floor plan ${floorplan.name || ''}`} className={styles.floorPlanImage} />
          </button>
        </div>
      )}
      <div className={compact ? styles.compactUnitTableWrapper : undefined}>
        <UnitTable units={floorplan.units} floorplan={floorplan} onUnitSelect={onUnitSelect} />
      </div>
      {lightboxPhotos.length > 0 && (
        <PhotoLightbox photos={lightboxPhotos} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />
      )}
    </Card>
  );
});
